import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Alert } from "../components/ui/Alert";
import { Field, Input, Switch } from "../components/ui/Field";

const API_URL = import.meta.env.VITE_API_URL || "";

type SsoSettings = {
  isSsoEnabled?: boolean;
  tenantId?: string;
  clientId?: string;
  clientSecret?: string;
  redirectUrl?: string;
  frontendUrl?: string;
};

export default function SsoSettingsPage({ token }: { token: string }) {
  const [settings, setSettings] = useState<SsoSettings>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      const res = await fetch(`${API_URL}/api/admin/sso-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(await res.json());
    };
    fetchSettings();
  }, [token]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);

    await fetch(`${API_URL}/api/admin/sso-settings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(settings),
    });

    setLoading(false);
    setSuccess(true);
  };

  const set = <K extends keyof SsoSettings>(key: K, value: SsoSettings[K]) =>
    setSettings((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="p-5">
      <header className="mb-4">
        <h1 className="text-ink text-lg font-semibold tracking-tight">SSO settings</h1>
        <p className="text-ink-muted mt-0.5 text-[13px]">
          Entra ID (Azure AD) single sign-on configuration.
        </p>
      </header>

      <Card className="max-w-2xl">
        <form onSubmit={handleSave} className="space-y-5">
          <Alert tone="warning" title="A backend restart is required">
            New SSO settings take effect only after the backend server restarts.
          </Alert>

          {success && <Alert tone="success">Settings saved.</Alert>}

          <Switch
            checked={settings.isSsoEnabled || false}
            onChange={(checked) => set("isSsoEnabled", checked)}
            label="Enable Entra ID SSO"
            hint="Shows the “Sign in with Microsoft” button on the login screen."
          />

          <div className="border-line space-y-4 border-t pt-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Directory (tenant) ID">
                {(props) => (
                  <Input
                    {...props}
                    value={settings.tenantId || ""}
                    onChange={(e) => set("tenantId", e.target.value)}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    className="font-mono text-xs"
                  />
                )}
              </Field>

              <Field label="Application (client) ID">
                {(props) => (
                  <Input
                    {...props}
                    value={settings.clientId || ""}
                    onChange={(e) => set("clientId", e.target.value)}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    className="font-mono text-xs"
                  />
                )}
              </Field>
            </div>

            <Field label="Client secret">
              {(props) => (
                <Input
                  {...props}
                  type="password"
                  value={settings.clientSecret || ""}
                  onChange={(e) => set("clientSecret", e.target.value)}
                  autoComplete="off"
                />
              )}
            </Field>

            <Field
              label="Redirect URI"
              hint="The backend callback. Must match the redirect URI registered in Entra ID exactly."
            >
              {(props) => (
                <Input
                  {...props}
                  value={settings.redirectUrl || ""}
                  onChange={(e) => set("redirectUrl", e.target.value)}
                  placeholder="https://schemunda.example.com/api/auth/microsoft/callback"
                />
              )}
            </Field>

            <Field label="Frontend URL" hint="Where users land after a successful sign-in.">
              {(props) => (
                <Input
                  {...props}
                  value={settings.frontendUrl || ""}
                  onChange={(e) => set("frontendUrl", e.target.value)}
                  placeholder="https://schemunda.example.com"
                />
              )}
            </Field>
          </div>

          <Button type="submit" size="md" variant="primary" disabled={loading}>
            {loading && (
              <span
                aria-hidden="true"
                className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/35 border-t-white"
              />
            )}
            {loading ? "Saving" : "Save settings"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
